import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ExternalLink, ChevronRight, Shield, Clock, Globe } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import api from '@/lib/api';
import { formatDateTime, getRiskBadgeClass, RISK_CATEGORIES_LABELS } from '@/lib/utils';
import { NavBar } from '@/components/NavBar';

const BriefingDetailPage = () => {
  const { briefingId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [briefing, setBriefing] = useState(null);

  useEffect(() => {
    const fetchBriefing = async () => {
      try {
        const res = await api.getBriefingDetail(briefingId);
        setBriefing(res.data);
      } catch (error) {
        console.error('Error fetching briefing:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBriefing();
  }, [briefingId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="animate-pulse text-[#4E6974]">Loading briefing...</div>
      </div>
    );
  }

  if (!briefing) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#4E6974] mb-4">Briefing not found</p>
          <Button onClick={() => navigate('/dashboard')} variant="outline" data-testid="return-dashboard-btn">Return to dashboard</Button>
        </div>
      </div>
    );
  }

  const impactDots = briefing.impact_rating || 3;
  const severity = impactDots >= 5 ? 'CRITICAL' : impactDots >= 4 ? 'HIGH' : impactDots >= 3 ? 'MEDIUM' : 'LOW';

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <NavBar />

      <main className="pt-24 px-4 lg:px-6 pb-8">
        <div className="max-w-[900px] mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="mt-6 text-[#4E6974] hover:text-[#0D3B4F]"
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to dashboard
          </Button>

          {/* Header */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-4 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Badge className={`${getRiskBadgeClass(severity)} text-[10px]`}>{severity}</Badge>
              <span className="text-xs text-[#4E6974]">{RISK_CATEGORIES_LABELS[briefing.signal_type] || briefing.signal_type}</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#0D3B4F] leading-tight mb-3" data-testid="briefing-title">
              {briefing.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-[#4E6974]">
              <div className="flex items-center gap-1.5">
                <Clock className="h-4 w-4" />
                {formatDateTime(briefing.created_at)}
              </div>
              <div className="flex items-center gap-1.5">
                <Globe className="h-4 w-4" />
                {briefing.country_name}
              </div>
              {briefing.source && (
                <span>Source: <span className="font-medium text-[#0D3B4F]">{briefing.source}</span></span>
              )}
              {briefing.url && (
                <a href={briefing.url} target="_blank" rel="noopener noreferrer" className="text-[#0097A7] hover:underline flex items-center gap-1" data-testid="source-link">
                  View original <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {briefing.country_tags?.map(tag => (
                <Badge key={tag} variant="secondary" className="bg-[#E0F7FA] text-[#0097A7] text-xs cursor-pointer" onClick={() => navigate(`/countries/${tag}`)}>{tag}</Badge>
              ))}
              {briefing.sector_tags?.map(tag => (
                <Badge key={tag} variant="secondary" className="bg-[#F5F5F5] text-[#4E6974] text-xs">{tag}</Badge>
              ))}
            </div>
          </motion.div>

          {/* Full Summary */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none mb-6">
              <CardHeader>
                <CardTitle className="text-lg font-bold text-[#0D3B4F]">Signal summary</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[#4E6974] leading-relaxed" data-testid="briefing-full-text">
                  {briefing.full_text || briefing.summary}
                </p>
                <div className="flex items-center gap-2 mt-4">
                  <span className="text-xs text-[#4E6974]">Impact:</span>
                  <div className="flex gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className={`w-2.5 h-2.5 rounded-full ${i < impactDots ? 'bg-[#00707C]' : 'bg-[#E0E0E0]'}`} />
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* TradePath Analysis */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none border-l-4 border-l-[#0097A7] mb-6">
              <CardHeader>
                <CardTitle className="text-lg font-bold text-[#0D3B4F] flex items-center gap-2" data-testid="tradepath-analysis-title">
                  <Shield className="h-5 w-5 text-[#0097A7]" />
                  TradePath analysis
                </CardTitle>
                <p className="text-xs text-[#4E6974] mt-1">Why this matters for global supply chains</p>
              </CardHeader>
              <CardContent>
                <p className="text-[#4E6974] leading-relaxed mb-6" data-testid="tradepath-analysis-text">
                  {briefing.tradepath_analysis}
                </p>

                {/* Sector impacts */}
                {briefing.sector_impacts?.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-[#0D3B4F] mb-3">Affected sectors</h4>
                    <div className="space-y-2">
                      {briefing.sector_impacts.map((impact, i) => (
                        <div key={i} className="p-3 rounded-lg bg-[#F5F5F5] text-sm text-[#4E6974]">{impact}</div>
                      ))}
                    </div>
                  </div>
                )}

                <Separator className="my-4" />

                {/* Recommended actions */}
                <h4 className="text-sm font-semibold text-[#0D3B4F] mb-3">Recommended actions</h4>
                <div className="space-y-2">
                  {briefing.recommended_actions?.map((action, i) => (
                    <div key={i} className="flex gap-3 items-start" data-testid={`action-item-${i}`}>
                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#00707C] text-white text-xs flex items-center justify-center font-bold mt-0.5">{i + 1}</span>
                      <p className="text-sm text-[#4E6974]">{action}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Related Briefings */}
          {briefing.related_briefings?.length > 0 && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
              <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none mb-6">
                <CardHeader>
                  <CardTitle className="text-lg font-bold text-[#0D3B4F]">Related briefings</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {briefing.related_briefings.map((rel) => (
                      <div
                        key={rel.id}
                        className="p-4 rounded-lg border border-[#E0E0E0] hover:border-[#0097A7] hover:shadow-md transition-all cursor-pointer group"
                        onClick={() => navigate(`/briefings/${rel.id}`)}
                        data-testid={`related-briefing-${rel.id}`}
                      >
                        <h4 className="text-sm font-semibold text-[#0D3B4F] group-hover:text-[#0097A7] transition-colors line-clamp-2">{rel.title}</h4>
                        <div className="flex items-center gap-2 mt-2 text-xs text-[#4E6974]">
                          <span>{rel.country_name || rel.country_tags?.[0]}</span>
                          <span>•</span>
                          <span>{formatDateTime(rel.created_at)}</span>
                          <ChevronRight className="h-3 w-3 ml-auto text-[#4E6974] group-hover:text-[#0097A7]" />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
};

export default BriefingDetailPage;
